import type { ReactNode } from "react";
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { FeatureFlagsManager, type FeatureFlagKey } from "@rebuild/shared";
import { useSelector } from "@/redux/store";
import { appSelector } from "@/redux/selectors/app.selector";
import featureFlagsProvider from "@/providers/feature-flags.provider";

interface FeatureFlagsContextType {
  isFeatureEnabled: (key: FeatureFlagKey) => boolean;
  loading: boolean;
  refetchFeatures: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

// Create a singleton instance of the manager
const featureFlagsManager = new FeatureFlagsManager();

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const user = useSelector(appSelector.user);
  const organizationId = useSelector(appSelector.organizationId);

  const fetchFeatures = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const response = await featureFlagsProvider.getFeatureFlags();
      if (response.success && response.data.featureFlags) {
        featureFlagsManager.setFeatures(response.data.featureFlags);
      }
    } catch (error) {
      console.error("Failed to fetch feature flags:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures, user]);

  const isFeatureEnabled = (key: FeatureFlagKey): boolean => {
    // Get environment variable with VITE_ prefix
    const envGetter = (envKey: string) => {
      return import.meta.env[envKey];
    };

    return featureFlagsManager.isFeatureEnabled(key, organizationId, envGetter, "VITE_");
  };

  const refetchFeatures = async () => {
    setLoading(true);
    await fetchFeatures();
  };

  return (
    <FeatureFlagsContext.Provider
      value={{
        isFeatureEnabled,
        loading,
        refetchFeatures,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within FeatureFlagsProvider");
  }
  return context;
}
